import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { levelToScale, type RecordHint } from '@/lib/micLevel';

/** Как быстро опускается шкала за кадр: подъём мгновенный, спад плавный — полоса не дёргается. */
const LEVEL_FALL = 0.85;

export type RecordPanelPhase = 'preparing' | 'recording' | 'paused';

type Props = {
  phase: RecordPanelPhase;
  /** Подсказка по уровню звука; `null` — звук в порядке или судить не по чему. */
  hint: RecordHint;
  /** Уровень 0…1 или `null`, если измерителя нет (нет WebAudio). */
  level: (() => number) | null;
};

/**
 * Панель записи в самом задании: «Готовим микрофон» → «Говорите», подсказка и
 * крупная шкала уровня (решение владельца 14.09.2026).
 *
 * 🔴 ЗАЧЕМ. Разбор записей 13–14.09: 108 отказов из 144 — тишина в файле, 36 —
 * короче 5 секунд, 13 — «нажал Записать и сразу Стоп». Шкала уровня была только в
 * строке вверху экрана, 8 px, и на iPhone её не замечали; а пока браузер открывает
 * микрофон (около 2 с), крутилась кнопка — и человек не знал, когда говорить.
 *
 * ⚠️ ТОЛЬКО РИСУЕТ. Рекордера, потока и часов здесь нет: подсказка на экране не
 * имеет права повлиять на запись. Сторож — `recordHintsRender.test.ts`.
 *
 * ⚠️ МЕСТО ПОД ПОДСКАЗКУ ЗАНЯТО ВСЕГДА, пока идёт запись: иначе появление плашки
 * сдвигало бы шкалу и кнопки, и блок дёргался бы под пальцем.
 */
export function RecordLevelPanel({ phase, hint, level }: Props) {
  const { t } = useTranslation();
  const barRef = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (phase !== 'recording' || !level) return;
    let alive = true;
    let frame = 0;
    let shown = 0;
    const tick = () => {
      if (!alive) return;
      const target = levelToScale(level());
      shown = target >= shown ? target : Math.max(target, shown * LEVEL_FALL);
      const bar = barRef.current;
      if (bar) bar.style.transform = `scaleX(${Math.max(0.02, shown)})`;
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => {
      alive = false;
      cancelAnimationFrame(frame);
    };
  }, [phase, level]);

  return (
    <div className="space-y-2 rounded-card border border-outline bg-surface-container-low p-3" data-record-panel={phase}>
      {phase === 'preparing' ? (
        <p role="status" className="flex items-center gap-2 text-base font-semibold text-on-surface-variant">
          <span className="material-symbols-outlined animate-spin text-xl" aria-hidden="true">
            progress_activity
          </span>
          {t('blockRenderers.recordPreparing')}
        </p>
      ) : phase === 'paused' ? (
        <p role="status" className="flex items-center gap-2 text-xl font-bold text-on-surface-variant">
          <span className="material-symbols-outlined text-2xl" aria-hidden="true">
            pause
          </span>
          {t('blockRenderers.audioPaused')}
        </p>
      ) : (
        <p role="status" className="flex items-center gap-2 text-xl font-bold text-on-surface">
          <span className="material-symbols-outlined text-2xl text-primary" aria-hidden="true">
            mic
          </span>
          {t('blockRenderers.recordSpeakNow')}
        </p>
      )}
      {phase === 'recording' && level && (
        <>
          <div className="min-h-14" aria-live="polite">
            {hint && (
              <p
                role="status"
                data-record-hint={hint}
                className="flex items-center gap-2 rounded-card bg-primary-container px-3 py-2 text-sm font-semibold text-on-primary-container"
              >
                <span className="material-symbols-outlined flex-none text-xl" aria-hidden="true">
                  {hint === 'none' ? 'mic_off' : 'volume_up'}
                </span>
                {hint === 'none' ? t('blockRenderers.recordHintNotHeard') : t('blockRenderers.recordHintLouder')}
              </p>
            )}
          </div>
          <span className="block h-4 w-full overflow-hidden rounded-pill bg-surface-container-highest">
            <span
              ref={barRef}
              data-record-level=""
              className="block h-full origin-left rounded-pill bg-primary"
              style={{ transform: 'scaleX(0.02)' }}
            />
          </span>
        </>
      )}
    </div>
  );
}
